import { lazy, ReactElement, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { hasAuthParams } from "react-oidc-context";
import { useAtomValue, useSetAtom } from "jotai";
import { fetchUserPreferencesAtom, userPreferencesAtom } from "atoms";
import { fetchQuotaUsageAtom } from "atoms/quota";
import BrowserDisclaimer from "shared/components/BrowserDisclaimer";
import Navbar from "shared/containers/Navbar";
import { useOIDCLogin } from "shared/hooks/useOIDCLogin";
import { RELEASE_NOTES_URL } from "shared/constants";
import { PATHS } from "shared/constants/paths";
import AccessDeniedNotification from "shared/components/AccessDeniedNotification";

const Homepage = lazy(() => import("features/Homepage"));
const SSML = lazy(() => import("features/SSML"));
const TTSHomepage = lazy(() => import("features/TTS"));
const MarkdownRenderer = lazy(
  () => import("shared/containers/MarkdownRenderer")
);
const CheatSheet = lazy(() => import("features/SSML/CheatSheet"));
const ThirdPartyNotices = lazy(() =>
  import("@web-applications/allinga-web-components").then((module) => ({
    default: module.ThirdPartyNotices,
  }))
);
const Copyright = lazy(() =>
  import("@web-applications/allinga-web-components").then((module) => ({
    default: module.Copyright,
  }))
);

const routeFallback = (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress />
  </Box>
);

export default function AppRoutes(): ReactElement {
  const { auth, hasAllingaStageTTSAccess } = useOIDCLogin();
  const fetchUserPreferences = useSetAtom(fetchUserPreferencesAtom);
  const authenticated = auth.isAuthenticated && hasAllingaStageTTSAccess;
  const fetchQuota = useSetAtom(fetchQuotaUsageAtom);
  // get auto-login params from URL
  const urlParams = new URLSearchParams(window.location.search);
  const hasAutoLoginParams = urlParams.get("auto-login") === "true";
  const userPreferences = useAtomValue(userPreferencesAtom);
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) fetchUserPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoading]);

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && userPreferences._id) {
      fetchQuota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences._id]);

  useEffect(() => {
    const hasParams = hasAuthParams();
    const isAuth = auth.isAuthenticated;

    if (!hasParams && !isAuth) {
      if (hasAutoLoginParams) {
        auth.signinRedirect();
      } else {
        auth.signinSilent();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  // wait for the authentication to be loaded, true or false
  if (auth.isLoading || auth.activeNavigator) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (auth.isAuthenticated && !hasAllingaStageTTSAccess)
    return <AccessDeniedNotification />;

  return (
    <>
      {!auth.isLoading && <Navbar />}
      <BrowserDisclaimer />
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route
            path={PATHS.root}
            element={authenticated ? <TTSHomepage /> : <Homepage />}
          />
          <Route
            path={PATHS.releaseNotes}
            element={
              authenticated ? (
                <MarkdownRenderer url={RELEASE_NOTES_URL} />
              ) : (
                <Homepage />
              )
            }
          />
          <Route
            path={PATHS.ssmlGuide}
            element={authenticated ? <SSML /> : <Homepage />}
          />
          <Route path={PATHS.ssmlCheatSheet} element={<CheatSheet />} />
          <Route
            path={PATHS.thirdPartyNotices}
            element={<ThirdPartyNotices />}
          />
          <Route path={PATHS.copyright} element={<Copyright />} />

          {/* Redirect any URL to root */}
          <Route path="*" element={<Navigate to={PATHS.root} />} />
        </Routes>
      </Suspense>
    </>
  );
}
